import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTableUiTheme } from '../../contexts/TableUiThemeContext';
import { cn } from '../../lib/utils';
import {
  renderSplitViewFieldValue,
  splitViewFieldColSpanClass,
  splitViewFieldValueContainerClass,
} from './splitViewFieldDisplay';

/**
 * Shared formSections field grid for SplitViewPanel and module *DetailsTab components.
 * Uses the same cell renderers / badge theme as data tables.
 *
 * @param {'boxed'|'plain'} layout — boxed: SplitViewPanel style; plain: minimal labels (Change Request, NCR)
 */
const SplitViewFieldsGrid = ({
  selectedRecord,
  fields = [],
  config,
  layout = 'boxed',
  fieldOverride,
  emptyLabel,
  skipTypes = ['hidden'],
  className,
}) => {
  const { theme } = useTheme();
  const { themeId: tableUiThemeId } = useTableUiTheme();
  void tableUiThemeId;

  if (!selectedRecord || !config?.formSections?.length) {
    return null;
  }

  const isPlain = layout === 'plain';

  return (
    <div className={cn('grid grid-cols-1 gap-4', className)}>
      {config.formSections.map((section, idx) => (
        <div key={idx}>
          <div className="flex items-center gap-2 mb-2">
            {section.icon && (
              <span className="text-base" aria-hidden="true">
                {section.icon}
              </span>
            )}
            <div className={cn('text-sm font-semibold', theme.text.primary)}>{section.title}</div>
          </div>
          {section.layout === 'custom' && section.customComponent ? (
            <section.customComponent
              deviationId={selectedRecord?.id}
              formData={selectedRecord}
              setFormData={undefined}
              config={config}
              fields={fields}
              isEditMode={false}
              selectedRecord={selectedRecord}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {section.fields.map((fieldName) => {
                const f = fields.find((ff) => ff.name === fieldName);
                if (!f || skipTypes.includes(f.type)) return null;

                if (f.dependsOn) {
                  const { field: dependsOnField, value: dependsOnValue } = f.dependsOn;
                  const dependsOnFieldValue = selectedRecord?.[dependsOnField];
                  let shouldShow = false;
                  if (typeof dependsOnValue === 'function') {
                    shouldShow = !!dependsOnValue(selectedRecord, config?.lookupData || {});
                  } else {
                    shouldShow = dependsOnFieldValue === dependsOnValue;
                  }
                  if (!shouldShow) return null;
                }

                const colSpan = splitViewFieldColSpanClass(f, fieldName, section);

                const content = renderSplitViewFieldValue(f, selectedRecord, theme, {
                  fieldOverride,
                  emptyLabel: isPlain ? emptyLabel || 'Not specified' : undefined,
                });

                if (isPlain) {
                  const ctx = selectedRecord[f.name];
                  const isEmpty = ctx == null || ctx === '';
                  return (
                    <div key={fieldName} className={cn('space-y-1', colSpan)}>
                      <div className={cn('text-xs font-medium', theme.text.secondary)}>
                        {f.label}
                      </div>
                      <div
                        className={cn(
                          'text-sm',
                          isEmpty && !fieldOverride ? theme.text.muted : theme.text.primary
                        )}
                      >
                        {content}
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={fieldName} className={colSpan}>
                    <div className={cn('text-xs mb-1', theme.text.secondary)}>{f.label}</div>
                    <div className={splitViewFieldValueContainerClass(f, fieldName, theme)}>
                      {content}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SplitViewFieldsGrid;
